/**
 * DeviceHealthBadge Component
 * 
 * Displays aggregated health status for a device with visual indicator
 * Replaces the simple WS (WebSocket) column with comprehensive health status
 * 
 * Health States Priority:
 * OFFLINE > ERROR > WARNING > DEGRADED > HEALTHY > UNKNOWN
 * 
 * Props:
 * - health: Health object from backend API
 * - onClick: Optional click handler for navigation to detail page
 * - showLabel: Show text label (default: false, icon only)
 */

import React from 'react';
import { Circle, HelpCircle } from 'lucide-react';

const DeviceHealthBadge = ({ health, onClick, showLabel = false }) => {
  // If no health data, show unknown
  if (!health || !health.overallHealth) {
    return (
      <span 
        className="device-health-badge"
        style={{
          fontSize: '18px',
          cursor: onClick ? 'pointer' : 'default',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px'
        }}
        title="Stato salute sconosciuto"
        onClick={onClick}
      >
        <HelpCircle size={16} color="#94a3b8" aria-hidden="true" />
        {showLabel && <span style={{ fontSize: '11px', color: '#999' }}>UNKNOWN</span>}
      </span>
    );
  }

  const { overallHealth } = health;

  // Health status configuration
  const healthConfig = {
    HEALTHY: {
      label: 'HEALTHY',
      color: '#28a745',
      title: 'Device operativo - tutti i sistemi funzionanti'
    },
    DEGRADED: {
      label: 'DEGRADED',
      color: '#ffc107',
      title: 'Device operativo ma con performance ridotte'
    },
    WARNING: {
      label: 'WARNING',
      color: '#ff9800',
      title: 'Device richiede attenzione - possibili problemi'
    },
    ERROR: {
      label: 'ERROR',
      color: '#dc3545',
      title: 'Device ha riportato errori'
    },
    OFFLINE: {
      label: 'OFFLINE',
      color: '#6c757d',
      title: 'Device non raggiungibile - offline da oltre 10 minuti'
    },
    UNKNOWN: {
      label: 'UNKNOWN',
      color: '#999',
      title: 'Stato salute sconosciuto'
    }
  };

  const config = healthConfig[overallHealth] || healthConfig.UNKNOWN;

  // Build detailed tooltip with comprehensive information
  const buildTooltip = () => {
    let tooltip = '';
    
    // Main status explanation
    switch (overallHealth) {
      case 'HEALTHY':
        tooltip = 'HEALTHY - Tutto funziona correttamente\n• WebSocket e API online (< 2 min)\n• Lettore Stripe Terminal connesso\n• Nessun errore rilevato';
        break;
      case 'DEGRADED':
        tooltip = 'DEGRADED - Performance degradate\n• Uno o entrambi i canali (WebSocket/API) hanno heartbeat rallentati (2-10 min)\n• Possibile app in background o standby\n• Device parzialmente funzionante';
        break;
      case 'WARNING':
        tooltip = 'WARNING - Richiede attenzione\n• Un canale offline (WebSocket o API)\n• Oppure lettore Stripe Terminal disconnesso\n• Device parzialmente raggiungibile';
        break;
      case 'ERROR':
        tooltip = 'ERROR - Errore rilevato\n• Il device ha segnalato errori recenti\n• Controllare la sezione "Ultimo Errore"\n• Possibili problemi operativi';
        break;
      case 'OFFLINE':
        tooltip = 'OFFLINE - Device non raggiungibile\n• Nessun heartbeat da oltre 10 minuti\n• WebSocket e API entrambi offline\n• Device probabilmente spento o senza connessione';
        break;
      case 'UNKNOWN':
      default:
        tooltip = 'UNKNOWN - Stato sconosciuto\n• Il device non ha mai inviato dati\n• Mai connesso o appena provisionato\n• In attesa del primo heartbeat';
        break;
    }
    
    // Add current status details if available
    if (health.websocket?.status || health.api?.status) {
      tooltip += '\n\nStatus corrente:';
      if (health.websocket?.status) {
        tooltip += `\n• WebSocket: ${health.websocket.status}`;
      }
      if (health.api?.status) {
        tooltip += `\n• API: ${health.api.status}`;
      }
      if (health.reader?.connected !== undefined) {
        tooltip += `\n• Lettore: ${health.reader.connected ? 'Connesso' : 'Disconnesso'}`;
      }
    }
    
    // Add last error if present
    if (health.health?.lastError) {
      tooltip += `\n\nUltimo errore:\n${health.health.lastError.substring(0, 80)}${health.health.lastError.length > 80 ? '...' : ''}`;
    }
    
    return tooltip;
  };

  return (
    <span
      className="device-health-badge"
      style={{
        fontSize: '18px',
        cursor: onClick ? 'pointer' : 'help',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        transition: 'transform 0.1s ease',
      }}
      title={buildTooltip()}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'scale(1.1)';
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
    >
      {overallHealth === 'UNKNOWN'
        ? <HelpCircle size={16} color={config.color} aria-hidden="true" />
        : <Circle size={13} color={config.color} fill={config.color} aria-hidden="true" />}
      {showLabel && (
        <span 
          style={{ 
            fontSize: '11px', 
            color: config.color,
            fontWeight: 600
          }}
        >
          {config.label}
        </span>
      )}
    </span>
  );
};

export default DeviceHealthBadge;
